import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Packfreund Gratis-Geschenk
 *
 * Hält den Warenkorb mit der Aktion synchron, egal ob er über das Theme,
 * den Essential-Cart-Drawer oder die Warenkorbseite geändert wird:
 *  - genug qualifizierende Sets im Warenkorb  -> Geschenk (1 Stück) wird hinzugefügt
 *  - zu wenige Sets                           -> Geschenk wird entfernt
 *  - Geschenk mehr als 1x                     -> wird auf 1 zurückgesetzt
 *  - Kunde entfernt das Geschenk selbst       -> wird nicht erneut aufgedrängt,
 *                                                bis er wieder ein Set-Angebot wählt
 *
 * Der Preis von 0 € kommt vom Geschenk-Produkt selbst (Preis 0,00 €, Vergleichspreis = Wert).
 * Es ist kein Shopify-Rabatt nötig und es kollidiert nicht mit den Set-Rabatten.
 */

const GIFT_PROPERTY = '_packfreund_gift';
const OWN_REQUEST_HEADER = 'X-Packfreund-Gift';
const STATE_KEY = 'packfreund-gift-state';
const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

const configElement = document.getElementById('packfreund-gift-config');
let config = null;

try {
  config = configElement ? JSON.parse(configElement.textContent || '{}') : null;
} catch {
  config = null;
}

const routes = () => {
  const themeRoutes = window.Theme?.routes || {};
  const root = window.Shopify?.routes?.root || '/';
  return {
    cart: `${themeRoutes.cart_url || `${root}cart`}.js`,
    add: themeRoutes.cart_add_url || `${root}cart/add.js`,
    update: `${themeRoutes.cart_update_url || `${root}cart/update`}.js`,
  };
};

function readState() {
  try {
    return JSON.parse(sessionStorage.getItem(STATE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeState(state) {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    // Privater Modus o. ä. – dann eben ohne Merker.
  }
}

function isOwnRequest(init) {
  const headers = init?.headers;
  if (!headers) return false;
  if (headers instanceof Headers) return headers.has(OWN_REQUEST_HEADER);
  if (Array.isArray(headers)) return headers.some(([name]) => name === OWN_REQUEST_HEADER);
  return OWN_REQUEST_HEADER in headers;
}

function requestUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input?.url || '';
}

async function cartRequest(url, body) {
  const options = {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', [OWN_REQUEST_HEADER]: '1' },
  };

  if (body) {
    options.headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }

  // window.fetch (nicht die native Referenz), damit auch der Essential-Cart-Drawer
  // unsere Änderungen mitbekommt und sich neu rendert.
  const response = await window.fetch(url, options);
  const data = await response.json();
  if (!response.ok || data?.status) {
    throw new Error(data?.description || data?.message || 'Warenkorb-Anfrage fehlgeschlagen');
  }
  return data;
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
    .filter(Boolean);
}

function analyseCart(cart) {
  const qualifyingIds = new Set((config.qualifyingProductIds || []).map(Number));
  const giftVariantId = Number(config.giftVariantId);
  let qualifyingQuantity = 0;
  const giftLines = [];

  for (const item of cart.items || []) {
    if (Number(item.variant_id) === giftVariantId && item.properties?.[GIFT_PROPERTY]) {
      giftLines.push(item);
      continue;
    }
    if (qualifyingIds.has(Number(item.product_id))) qualifyingQuantity += item.quantity;
  }

  return {
    qualifyingQuantity,
    qualifies: qualifyingQuantity >= Number(config.minQuantity || 2),
    giftLines,
    giftQuantity: giftLines.reduce((total, item) => total + item.quantity, 0),
  };
}

/** Informiert Horizon (Warenkorbseite, Zähler am Icon) über unsere Änderung. */
function announceCartChange(cart, sections) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.body.dispatchEvent(
    new CartLinesUpdateEvent({
      // Bewusst 'update' statt 'add': Horizon öffnet sonst seinen eigenen Drawer.
      action: 'update',
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      didError: false,
      items: cart.items,
      itemCount: cart.item_count,
      sections,
      source: 'packfreund-gift',
    },
  });
}

let running = false;
let rerunRequested = false;
let reconcileTimer;

async function reconcile() {
  if (!config) return;
  if (running) {
    rerunRequested = true;
    return;
  }

  running = true;
  try {
    const { cart: cartUrl, add: addUrl, update: updateUrl } = routes();
    const cart = await cartRequest(cartUrl);
    const { qualifies, giftLines, giftQuantity } = analyseCart(cart);
    const state = readState();
    const sectionIds = cartSectionIds();
    const sectionPayload = sectionIds.length
      ? { sections: sectionIds.join(','), sections_url: window.location.pathname }
      : {};

    if (!qualifies) {
      state.declined = false;
      state.hadGift = false;

      if (giftLines.length) {
        const updates = Object.fromEntries(giftLines.map((item) => [item.key, 0]));
        const updatedCart = await cartRequest(updateUrl, { updates, ...sectionPayload });
        announceCartChange(updatedCart, updatedCart.sections);
      }
    } else if (giftQuantity === 0) {
      // Geschenk war da, Sets reichen weiterhin -> Kunde hat es bewusst entfernt.
      if (state.hadGift) state.declined = true;
      state.hadGift = false;

      if (!state.declined && config.giftAvailable !== false) {
        const response = await cartRequest(addUrl, {
          items: [{ id: Number(config.giftVariantId), quantity: 1, properties: { [GIFT_PROPERTY]: '1' } }],
          ...sectionPayload,
        });
        const updatedCart = await cartRequest(cartUrl);
        announceCartChange(updatedCart, response.sections);
        state.hadGift = true;
      }
    } else {
      state.hadGift = true;

      if (giftQuantity > 1) {
        const updates = Object.fromEntries(giftLines.map((item, index) => [item.key, index === 0 ? 1 : 0]));
        const updatedCart = await cartRequest(updateUrl, { updates, ...sectionPayload });
        announceCartChange(updatedCart, updatedCart.sections);
      }
    }

    writeState(state);
  } catch (error) {
    console.warn('[packfreund-gift]', error);
  } finally {
    running = false;
    if (rerunRequested) {
      rerunRequested = false;
      scheduleReconcile();
    }
  }
}

function scheduleReconcile(delay = 250) {
  clearTimeout(reconcileTimer);
  reconcileTimer = setTimeout(reconcile, delay);
}

if (config && !window.PackfreundGift) {
  // Öffentliche Schnittstelle für das Mengen-Angebot (packfreund-bundle-offers.js).
  window.PackfreundGift = {
    config,
    property: GIFT_PROPERTY,
    ownRequestHeader: OWN_REQUEST_HEADER,
    analyseCart,
    /** Kunde wählt aktiv ein Set-Angebot -> Geschenk darf wieder automatisch rein. */
    acceptGift() {
      writeState({ ...readState(), declined: false, hadGift: false });
    },
    reconcile: scheduleReconcile,
  };

  // Warenkorb-Änderungen per fetch (Theme, Essential Cart, andere Apps).
  const originalFetch = window.fetch;
  window.fetch = function packfreundGiftFetch(input, init) {
    const result = originalFetch.apply(this, arguments);
    try {
      if (!isOwnRequest(init) && CART_MUTATION_PATTERN.test(requestUrl(input))) {
        result.then((response) => response.ok && scheduleReconcile()).catch(() => {});
      }
    } catch {
      // Beobachtung darf den eigentlichen Request nie stören.
    }
    return result;
  };

  // Warenkorb-Änderungen per XMLHttpRequest (ältere App-Skripte, jQuery).
  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function packfreundGiftOpen(method, url) {
    try {
      if (CART_MUTATION_PATTERN.test(String(url))) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) scheduleReconcile();
        });
      }
    } catch {
      // ignorieren
    }
    return originalOpen.apply(this, arguments);
  };

  // Änderungen über Shopifys Standard-Events (z. B. Shopify.actions.updateCart).
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(({ detail } = {}) => {
        if (detail?.source !== 'packfreund-gift') scheduleReconcile();
      })
      .catch(() => {});
  });

  // Beim Laden und beim Zurück-Navigieren (bfcache) einmal prüfen.
  window.addEventListener('pageshow', () => scheduleReconcile(400));
  scheduleReconcile(400);
}
